import { z } from "zod";

/**
 * Normalize user phone input to E.164 (what the backend's SMS pipeline
 * expects). Accepts common formatting (spaces, dashes, parens); a bare
 * 10-digit number is assumed to be Canadian/US national format and gets
 * a +1 prefix. Returns null when the input can't be a valid E.164 number.
 */
export const normalizePhoneE164 = (raw: string): string | null => {
  const cleaned = raw.trim().replace(/[\s().-]/g, "");
  const hasPlus = cleaned.startsWith("+");
  const digits = hasPlus ? cleaned.slice(1) : cleaned;
  if (!/^\d{8,15}$/.test(digits)) return null;

  const e164 = hasPlus
    ? `+${digits}`
    : digits.length === 10
      ? `+1${digits}`
      : `+${digits}`;

  return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
};

export const signupSchema = z
  .object({
    type: z.enum(["homeowner", "contractor"]),
    name: z
      .string()
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters")
      .regex(
        /^[a-zA-Z\s\-]+$/,
        "Name can only contain letters, spaces, and hyphens"
      ),
    email: z
      .string()
      .email("Please enter a valid email address")
      .min(5)
      .max(254, "Email must not exceed 254 characters"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(50, "Password must not exceed 50 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain uppercase, lowercase, and numbers"
      ),
    // Contractors verify via SMS OTP, so the backend's RegisterContractor
    // endpoint requires a mobile number; homeowners never see this field.
    mobilePhone: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.type !== "contractor") return;

    if (!values.mobilePhone || values.mobilePhone.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mobilePhone"],
        message: "Mobile phone is required for contractors",
      });
      return;
    }

    if (!normalizePhoneE164(values.mobilePhone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mobilePhone"],
        message: "Please enter a valid mobile phone number",
      });
    }
  });

export type SignupFormValues = z.infer<typeof signupSchema>;
